<template>
	<v-app class="console-expense" :theme="theme">
		<router-view
			name="appbar"
			:class="appBarClasses"
			@toggle-rail="toggleRail"
			:auth="auth"
			:no-toggle-rail="!auth"
			:rail="rail"
			@toggle-billing-report="openBillingDialog"
		/>
		<router-view
			name="nav"
			temporary
			:rail="rail"
			:entries="entries"
			:fixed-nav-entries="fixedNavEntries"
			:app-nav-title="appNavTitle"
		/>
		<app-notifications />
		<v-main class="ce-main">
			<router-view />
			<billing-activity-dialog :model-value="billingDialogVisible" @update:model-value="closeBillingDialog" />
		</v-main>
		<responsive-mobile-banner v-if="isMobile" />
	</v-app>
</template>

<style lang="scss" scoped>
.ce-main {
	position: relative;
	display: flex;
	flex-direction: column;
	min-height: calc(100vh - var(--ce-app-bar-height));
	height: calc(100vh - var(--ce-app-bar-height));
}
</style>

<script lang="ts" setup>
import { computed, provide, readonly } from 'vue';
import { useRoute } from 'vue-router';
import { VApp, VMain } from 'vuetify/components';

import { Locale, useBoolean, useLocale, useTheme } from '@silae/composables';
import { AppNotifications, BillingActivityDialog, ResponsiveMobileBanner } from '~/components';
import { useFavicon, useNavigation, useResponsive } from '~/composables';
import { NavigationEntriesOptions } from '~/domain';
import { HomeRoute, ManageTemplatesRoute, SettingsRoute } from '~/pages';
import { updateLuxonConfiguration, updateVeeValidateConfig } from '~/plugins';
import { APP_NAVIGATION_RAIL_WIDTH, APP_NAVIGATION_WIDTH, APP_NAVIGATION_WIDTH_KEY } from '~/utils';

import { initBackend } from './app.init';

initBackend();

/** Composables and computed */
const { theme } = useTheme();
useFavicon();
const route = useRoute();

const auth = computed(() => !!route?.meta?.auth);
const { isMobile } = useResponsive();

const onLocaleUpdate = (newLocale: Locale) => {
	updateLuxonConfiguration(newLocale);
	updateVeeValidateConfig(newLocale);
};

useLocale(onLocaleUpdate);

const navigationOptions = computed<NavigationEntriesOptions>(() => {
	if (route?.fullPath?.includes(SettingsRoute.path)) {
		return {
			filterBySettingsNavEntry: true
		};
	}

	return {
		filterByNavEntry: true
	};
});
const fixedNavEntries = computed(() => [asNavigationEntry(HomeRoute), asNavigationEntry(ManageTemplatesRoute)]);
const { appNavTitle, asNavigationEntry, entries, rail, toggleRail } = useNavigation(navigationOptions);
const appBarClasses = computed(() => (rail.value ? 'app-bar--rail' : ''));
const appNavigationWidth = computed(() => (rail.value ? APP_NAVIGATION_RAIL_WIDTH : APP_NAVIGATION_WIDTH));

// Provide app navigation width globaly so that component may react to it (e.g: side panels)
provide(APP_NAVIGATION_WIDTH_KEY, readonly(appNavigationWidth));

const { value: billingDialogVisible, enable: openBillingDialog, disable: closeBillingDialog } = useBoolean();
</script>
